import { useNavigate } from "react-router";
import { useAuth } from "../auth";
import { roleLabel, roleSlug, roleTagline } from "../auth/roleMap";
import type { Role } from "@ub/shared";
import css from "./RoleSwitcher.module.css";

/**
 * B.1.3 — first-launch / multi-role picker. Fullscreen.
 *
 *   Header:  WELCOME, {NICKNAME}  + (★ FULL STACK 4/4) or (N OF 4) badge
 *   Body:    N role cards (role-color border, name CAPS, tagline, chevron)
 *
 * Stage 1d MVP — no pixel-art mascot yet; the corner-pulse frame from the
 * BootScreen serves as the visual anchor. Add the mascot in a Stage 5
 * polish pass.
 */
export function RoleSwitcher() {
  const { user, availableRoles, setActiveRole, signOut } = useAuth();
  const navigate = useNavigate();

  if (!user) return null;

  const nick = (user.nickname || user.name || "GUEST").toUpperCase();
  const isFullStack = user.fullstack && availableRoles.length === 4;

  const pick = (role: Role) => {
    setActiveRole(role);
    navigate(`/${roleSlug(role)}`, { replace: true });
  };

  return (
    <main className={css.root}>
      <div className={css.frame}>
        <span className={`${css.corner} ${css.tl}`} aria-hidden="true" />
        <span className={`${css.corner} ${css.tr}`} aria-hidden="true" />
        <span className={`${css.corner} ${css.bl}`} aria-hidden="true" />
        <span className={`${css.corner} ${css.br}`} aria-hidden="true" />

        <header className={css.head}>
          <p className={css.welcome}>
            WELCOME, <span className={css.nick}>{nick}</span>
          </p>
          <p className={css.sub}>
            @{user.nickname || user.staffId} · первый вход
          </p>
          {isFullStack ? (
            <span className={css.fullstack} title="Все 4 роли доступны">
              ★ FULL STACK 4/4
            </span>
          ) : (
            <span className={css.count}>
              {availableRoles.length} OF 4
            </span>
          )}
        </header>

        <p className={css.kicker}>ВЫБЕРИТЕ РОЛЬ {pad2(availableRoles.length)}/04</p>

        <div className={css.cards}>
          {availableRoles.map((role) => (
            <RoleCard key={role} role={role} onPick={pick} />
          ))}
        </div>

        <button type="button" className={css.signOut} onClick={signOut}>
          ← выйти из аккаунта
        </button>
      </div>
    </main>
  );
}

interface RoleCardProps {
  role: Role;
  onPick: (role: Role) => void;
}

function RoleCard({ role, onPick }: RoleCardProps) {
  return (
    <button
      type="button"
      className={css.card}
      data-role={roleSlug(role)}
      onClick={() => onPick(role)}
    >
      <span className={css.cardHex} aria-hidden="true">
        <Hex />
      </span>
      <span className={css.cardBody}>
        <span className={css.cardName}>{roleLabel(role)}</span>
        <span className={css.cardTagline}>{roleTagline(role)}</span>
      </span>
      <span className={css.cardChev} aria-hidden="true">
        →
      </span>
    </button>
  );
}

function Hex() {
  return (
    <svg
      width="24"
      height="26"
      viewBox="0 0 24 26"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinejoin="round"
    >
      <path d="M12 1 L22 7 L22 19 L12 25 L2 19 L2 7 Z" />
    </svg>
  );
}

function pad2(n: number) {
  return String(n).padStart(2, "0");
}
